
// ── GM directive subsystem ───────────────────────────────────────────────────
//
// The GM→clients directive channel, split **two ways** and carried on
// the SAME `/ws/game` socket (the Game room already authenticates the GM and holds
// every connected client, so it's the natural routing point):
//
//  (a) **Standing view-state** (`game_set_view`) — *durable*. Sets or clears a slot's
//      standing banner. Applied via the `set_slot_view`
//      command → persisted in `session.json` → **re-sent on reconnect** through the
//      existing `game_welcome.viewState` (a dropped/late player still sees the
//      banner). The GM also gets a live `game_view` push to every targeted, still-
//      connected client so the change shows *now*, not only on the next reconnect.
//
//  (b) **Ephemeral FX** (`game_fx`) — *fire-and-forget*. A one-shot client effect
//      (a screen glitch, a transient toast). **NOT persisted, NOT replayed** — a
//      client that reconnects after one fired never sees it. Delivered to matched
//      live sockets as `game_fx_event`.
//
// Both carry a **generic `Directive` envelope** (`{ type, payload? }`) so the client
// **effect registry** (frontend) keys handlers by `type` and a NEW effect needs no
// protocol change — only a registry registration. Directives are **GM-only**; the
// Game room rejects a `game_set_view` / `game_fx` from a non-`gm` slot.

/**
 * Which clients a directive targets — all clients, or specific slots. `"all"`
 * broadcasts to every connected client; a `slotIds` list routes
 * only to those slots' live sockets.
 */
export type DirectiveTarget = "all" | { slotIds: string[] };

/**
 * The **generic directive envelope** — the load-bearing extensibility seam. A
 * directive is just a `type` (the effect-registry key) plus an optional opaque
 * `payload`. The registry on the client maps `type` → a handler; adding `shake`,
 * `flash`, … is a one-line registration with NO change to this protocol. The
 * standing view-state directive (`banner`) reduces to a `set_slot_view` patch (below);
 * the ephemeral ones (`glitch`/…) ride `game_fx` verbatim.
 */
export type Directive = {
  /** The effect-registry key — `"banner"`, `"glitch"`, … */
  type: string;
  /** Opaque per-effect data (e.g. `{ text, image }`). Registry-interpreted. */
  payload?: unknown;
}

/**
 * How a standing banner looks on a player's screen: an OMNINET message band across the top, a
 * hazard-striped ALERT that blinks three times then holds, or NARRATION letterbox bars with the
 * line in the lower bar. A banner without one (a world saved before tones) reads as `omninet`.
 */
export type BannerTone = "omninet" | "alert" | "narration";

/** Every banner tone, in the Director's picker order. */
export const BANNER_TONES: readonly BannerTone[] = ["omninet", "alert", "narration"];

/** A standing banner: text always, an optional image, an optional {@link BannerTone}. */
export type DirectiveBanner = {
  text: string;
  /** Optional image URL/ref shown alongside the text (the text+image variant). */
  image?: string;
  /** The banner's look; absent reads as `omninet` ({@link bannerTone}). */
  tone?: BannerTone;
}

/** A banner's tone, defaulting an absent or unknown one to `omninet`. */
export function bannerTone(banner: { tone?: unknown }): BannerTone {
  return BANNER_TONES.find((t) => t === banner.tone) ?? "omninet";
}

/**
 * GM→clients **standing view-state** directive (the durable half). Sets or clears the
 * targeted slots' standing `activeBanner`. The server applies a `set_slot_view` per
 * targeted slot (persisted), then pushes a live `game_view` to each still-connected
 * target. A field left `undefined` is unchanged; an explicit `null` clears it. GM-only.
 */
export type GameSetViewMessage = {
  type: "game_set_view";
  target: DirectiveTarget;
  /** The standing banner to set, clear (null), or leave unchanged (undefined). */
  activeBanner?: DirectiveBanner | null;
}

/**
 * GM→clients **ephemeral FX** directive (the fire-and-forget half). Routed
 * to the matched live sockets as a `game_fx_event`, NEVER persisted or replayed.
 * GM-only.
 */
export type GameFxMessage = {
  type: "game_fx";
  target: DirectiveTarget;
  directive: Directive;
}